// -- Framework imports --
import { App, Plugin, watch } from 'vue';
import type { Router } from 'vue-router';
import axios from 'axios';

// -- Lib imports --
import { useAppSidebarMenuStore, type SidebarItemType } from '@lib/stores/app-sidebar-menu';
import type { User } from '@lib/types';

// -- App imports --
import type { Team } from '@/types';

// -- Define dynamic, context-aware sidebar content --
const SidebarPlugin: Plugin = {
	install: (app: App) => {
		const auth = app.config.globalProperties.$auth; //Todo: Can we get around this? useAuth can not used her because it is not inside setup
		const sidebarMenuStore = useAppSidebarMenuStore();
		const router: Router | undefined = app.config.globalProperties.$router;

		/**
		 * Fetches badges from backend and displays them
		 * on relevent items in the sidebar
		 */
		async function updateBadges() {
			// Only load badges after ensuring the environment allows for it
			if (!auth.user() || updateBadgesLock) return;

			try {
				updateBadgesLock = true;
				const { data: badges } = await axios.get<SidebarItemBadgeEntries>('/me/sidebar_badges');

				sidebarMenuStore.$state.items.forEach((item) => {
					// Ensure this sidebar item is a link item
					if (!('to' in item && item.to && 'label' in item && item.label)) return;

					// Search matching badge in API response and apply value and color to sidebar item
					const badge = badges[item.label];
					if (badge) {
						item.badge_text = badge.value ? badge.value.toString() : undefined;
						item.badge_color = badge.color;
					}
				});
			} finally {
				updateBadgesLock = false;
			}
		}

		// Prevents parallel calls of updateBadges if true
		let updateBadgesLock = false;

		// Load badges during each navigation step
		if (router) {
			router.afterEach(updateBadges);
		}

		watch(
			() => auth.user(),
			async (user: User) => {
				// Return empty sidebar if user if not logged in
				if (!user) {
					return;
				}

				// Get teams this user may manage
				const teams: Array<Team> = (await axios.get<Array<Team>>(`managing/teams`)).data;

				// Sidebar items shown to all users
				const sidebarMenu: Array<SidebarItemType> = [
					{ label: 'header_me', values: { firstname: user.firstname, lastname: user.lastname } },
					{ to: '/my/dashboard', icon: 'fa fa-home', label: 'my_dashboard' },
					{ to: '/my/voicemail', icon: 'fa fa-voicemail', label: 'my_voicemail' },
					{ to: '/my/phonebook', icon: 'fa fa-address-book', label: 'my_phonebook' },
					{ to: '/my/call_history', icon: 'fa fa-clock-rotate-left', label: 'my_call_history' },
				];

				// Show management area for each Team the user is allowed to manage
				if (Array.isArray(teams)) {
					teams.forEach((team) => {
						sidebarMenu.push(
							{ divider: true },
							{ label: 'header_manage', values: { team: team.name } },
							{
								to: `/manage/${team.id}/phones`,
								icon: 'fa fa-phone',
								label: 'phones',
								roles: ['manager'],
							},
							{
								to: `/manage/${team.id}/extensions`,
								icon: 'fa fa-hashtag',
								label: 'extensions',
								roles: ['manager'],
							},
							{
								to: `/manage/${team.id}/ring_groups`,
								icon: 'fa fa-users-rays',
								label: 'ring_groups',
								roles: ['manager'],
							},
							{
								to: `/manage/${team.id}/shortcut_buttons`,
								icon: 'fa fa-keyboard',
								label: 'shortcut_buttons',
								roles: ['manager'],
							},
							{
								to: `/manage/${team.id}/phonebook`,
								icon: 'fa fa-address-book',
								label: 'phonebook',
								roles: ['manager'],
							},
						);
					});
				}

				// Sidebar items shown to users with Admin role
				if (auth.check(['admin', 'global_admin'], 'roles')) {
					sidebarMenu.push(
						{ divider: true },
						{ label: 'header_admin' },
						{ to: '/admin/trunks', icon: 'fa fa-globe', label: 'trunks', roles: ['admin'] },
						{
							to: '/admin/call_records',
							icon: 'fa fa-clock-rotate-left',
							label: 'call_records',
							roles: ['admin'],
						},
						{
							to: '/admin/global_phonebook',
							icon: 'fa fa-address-book',
							label: 'global_phonebook',
							roles: ['admin'],
						},
						{ to: '/admin/users', icon: 'fa fa-users', label: 'users', roles: ['admin'] },
						{ to: '/admin/teams', icon: 'fa fa-user-group', label: 'teams', roles: ['admin'] },
						// { to: '/admin/settings', icon: 'fa fa-cogs', label: 'settings', roles: ['admin'] }
					);
				}

				sidebarMenuStore.set(sidebarMenu);
			},
		);
	},
};

// Used by updateBadges() above for showing badges in the sidebar
type SidebarItemBadgeEntries = {
	[key: string]: {
		// Label of the sidebar item this badge belongs to
		value: string | number | null; // Value of the badge on the sidebar item
		color?: string; // Color of the badge on the sidebar item
	};
};

export default SidebarPlugin;
